import {
  Component,
  HostListener,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { QuoterSidebarCoverageComponent } from '@webapp/quoter/ui/components/quoter-sidebar-coverage/quoter-sidebar-coverage.component';
import { ProducerSelectorModalComponent } from '@webapp/quoter/ui/components/producer-selector-modal/producer-selector-modal.component';
import { ShowForRoleDirective } from '@webapp/core/directives/showForRole';
import { IQuoteWithInsurable } from '@webapp/quotes/interfaces/quotes.interface';
import { IUser } from '@webapp/users/interfaces/users.interface';
import { QuotesStoreService } from '@webapp/quotes/store/quotes-store.service';
import { RISK_ISSUANCE_DRAFT, riskIconKey } from '@webapp/risk-core';
import { ButtonComponent, ModalComponent, IconType } from '@webapp/ui';
import { QUOTER_ROUTE_PATH } from '@webapp/core';
import { CLIENT_DASHBOARD_ROUTE_PATH } from '@webapp/core/constants/client-routes';
import { AuthService, UserRole } from '@webapp/auth';
import { IssuePolicyStore } from '../../store/issue-policy-store.service';

@Component({
  selector: 'app-issue-policy-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    ButtonComponent,
    ModalComponent,
    QuoterSidebarCoverageComponent,
    ProducerSelectorModalComponent,
    ShowForRoleDirective,
  ],
  providers: [{ provide: RISK_ISSUANCE_DRAFT, useExisting: IssuePolicyStore }],
  templateUrl: './issue-policy-layout.component.html',
  styleUrl: './issue-policy-layout.component.scss',
})
export class IssuePolicyLayoutComponent implements OnInit, OnDestroy {
  isLoading = false;
  isModalOpen = false;
  errors: string[] = [];
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  store = inject(QuotesStoreService);
  issuePolicyStore = inject(IssuePolicyStore);
  quote = this.store.get();
  getIconName = (tag: string): IconType => riskIconKey(tag) as IconType;

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnloadHandler(e: BeforeUnloadEvent) {
    e.preventDefault();
  }

  async ngOnInit(): Promise<void> {
    this.issuePolicyStore.clear();

    this.route.firstChild?.params.subscribe(async params => {
      const quoteId = params['id'];
      if (!quoteId) return;

      await this.store.detailStore.fetch(quoteId);
      const loadedQuote = this.store.get()();
      if (loadedQuote) {
        this.issuePolicyStore.initializeState(loadedQuote as IQuoteWithInsurable);
        this._resolveSelectedCoverage(loadedQuote as IQuoteWithInsurable);
      }
    });
  }



  private _resolveSelectedCoverage(quote: IQuoteWithInsurable): void {
    const coverages = quote.coverages ?? [];
    if (coverages.length === 0) return;
    const coverageId = this.route.snapshot.queryParams['coverageId'];
    const resolved =
      (coverageId && coverages.find(c => c.id === coverageId))
      ?? coverages.find(c => c.select)
      ?? coverages[0];
    if (resolved) this.issuePolicyStore.setSelectedCoverage(resolved);
  }

  getTitle() {
    return '';
  }

  openModal() {
    this.issuePolicyStore.openLeavingModal();
  }

  closeModal() {
    this.issuePolicyStore.closeLeavingModal();
  }

  closeOptionalCoverageModal() {
    this.issuePolicyStore.closeOptionalCoverageModal();
  }

  getCoverage() {
    return this.issuePolicyStore.getCoverage();
  }

  addOptionalCoverage() {
    this.issuePolicyStore.addOptionalCoverage();
  }

  closeQuoter() {
    // Clean up state when closing
    this.issuePolicyStore.clear();

    const isLoggedInClient =
      this.authService.isAuthenticated() &&
      this.authService.user()?.role === UserRole.CLIENT;

    this.router.navigate([isLoggedInClient ? CLIENT_DASHBOARD_ROUTE_PATH : QUOTER_ROUTE_PATH]);
  }

  // Modal visibility getters for template binding
  get isLeavingModalVisible() {
    return this.issuePolicyStore.isLeavingModal();
  }

  set isLeavingModalVisible(value: boolean) {
    if (value) {
      this.issuePolicyStore.openLeavingModal();
    } else {
      this.issuePolicyStore.closeLeavingModal();
    }
  }

  get isOptionalCoverageModalVisible() {
    return this.issuePolicyStore.isOptionalCoverageModal();
  }

  set isOptionalCoverageModalVisible(value: boolean) {
    if (!value) {
      this.issuePolicyStore.closeOptionalCoverageModal();
    }
  }

  isQuoted(): boolean {
    const params = this.route.snapshot.queryParams;
    return !!(params['quoteId'] && params['coverageId']);
  }

  ngOnDestroy(): void {
    // Clean up state when component is destroyed
    this.issuePolicyStore.clear();
  }

  
  setAgent(agent: IUser){
   this.issuePolicyStore.set('agent', agent);
  }
  clearUser() {
    this.issuePolicyStore.set('agent', {} as IUser);
  }
}